import { DialogTitle, GridProps, useTheme } from '@mui/material';
import { ContextForm, DatePicker, IFormAfterSubmit, IFormLookupFilterProps, InputOnlyField, IValidationError, Numeric, Select, Text } from '@ngt/forms';
import { FormManager, ValidateOn } from '@ngt/forms-core';
import { FC, useCallback, useContext, useRef } from 'react';
import { LookupItem, Permissions, ProjectPrecinct, Stage, StageRegistrationDateBases, StageStatuses } from '../api/dtos';
import StageDetailsContext from '../contexts/StageDetailsContext';
import StageSummariesContext from '../contexts/StageSummariesContext';
import StagesContext from '../contexts/data/StagesContext';
import usePermissions from '../hooks/auth/usePermissions';
import { DollarInput, FormSection, IFormDialogProps, PercentInput, StyledButton, StyledDialog, StyledDialogActions, StyledDialogContent, StyledDivider, StyledFormColumn, StyledFormSubmitButton } from './inputs/CommonFormElements';
import { DateTime } from 'luxon';
import LotSummariesContext from '../contexts/LotSummariesContext';

const COLUMN_SIZING: GridProps['size'] = {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 6,
    xl: 6
} 

const StageLookupFilter: IFormLookupFilterProps<Stage>['filterFunction'] = (formState, _propertyPath, lookup) => {
    if (!lookup) {
        return undefined;
    }

    return {
        ...lookup,
        items: lookup.items
            ?.filter(x => {
                return (x as LookupItem).projectId === formState.initialValue?.projectId
            })
            ?.sort(
                (s1, s2) => (s1.value as string) < (s2.value as string) ? -1 : 1
            ) ?? []
    }
}

const PrecinctLookupFilter: IFormLookupFilterProps<ProjectPrecinct>['filterFunction'] = (formState, _propertyPath, lookup) => {
    if(!lookup) {
        return undefined;
    }

    return {
        ...lookup,
        items: lookup.items
            ?.filter(x => {
                return (x as LookupItem).projectId === formState.initialValue?.projectId
            })
            ?.sort(
                (l1, l2) => (l1.value as string) < (l2.value as string) ? -1 : 1
            ) ?? []
    };
}

const StatusLookupFilter: IFormLookupFilterProps<Stage>['filterFunction'] = (formState, _propertyPath, lookup) => {
    if(!lookup) {
        return undefined;
    }

    return {
        ...lookup,
        items: lookup.items
            ?.map(x => {
                const disabled = (
                    ((x as LookupItem).id == StageStatuses.Registered && formState.value?.registrationDateBasis != StageRegistrationDateBases.Actual) || 
                    ((x as LookupItem).id != StageStatuses.Registered && formState.value?.registrationDateBasis == StageRegistrationDateBases.Actual)
                );

                return {...x, disabled: disabled};
            })
    };
}

const StageForm: FC<IFormDialogProps> = (props) => {
    const {data: permissions} = usePermissions();
    const formRef = useRef<FormManager<Stage, IValidationError>>(null);
    const theme = useTheme();
    const { refresh: refreshStageDetails } = useContext(StageDetailsContext);
    const { refresh: refreshStageSummaries } = useContext(StageSummariesContext);
    const { refresh: refreshStages } = useContext(StagesContext);
    const { refresh: refreshLotSummaries } = useContext(LotSummariesContext);

    const handleRegistrationDateBasisChange = (registrationDateBasis: StageRegistrationDateBases) => {
        if(registrationDateBasis == StageRegistrationDateBases.Actual) {
            formRef.current?.setFieldValue('status', StageStatuses.Registered, true, true, true, true);
        }
        else {
            formRef.current?.setFieldValue('status', undefined, true, true, true, true);
        }
    }

    const handleRegistrationDateChange = (registrationDate: DateTime) => {
        if(!formRef.current?.getFieldValue('registrationDateBasis') && registrationDate.isValid) {
            formRef.current?.setFieldValue('registrationDateBasis', StageRegistrationDateBases.Forecast, true, true, true, true);
        }
    }

    const onAfterSubmit: IFormAfterSubmit<Stage> = useCallback(async (_newValue, _formState, _formActions) => {
        if(refreshStageSummaries) {
            await refreshStageSummaries();
        }
        if(refreshStageDetails){
            await refreshStageDetails();
        }
        if(refreshStages) {
            await refreshStages();
        }
        if(refreshLotSummaries) {
            await refreshLotSummaries();
        }
        props.onClose();
    }, [refreshStageSummaries, refreshStageDetails, props.onClose])

    return (
        <ContextForm
            onAfterSubmit={onAfterSubmit}
            validateOn={ValidateOn.onChange}
            validateOnReset
            formRef={formRef}
        >
            <StyledDialog {...props}>
                <DialogTitle
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: "white"
                    }}
                >
                    {props.edit ? "Edit" : "Add"} Stage
                </DialogTitle>
                <StyledDialogContent>
                    <FormSection>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <InputOnlyField 
                                label="Name"
                                component={Text}
                                propertyPath={'name'}
                            />
                            <InputOnlyField
                                label="Development Status" 
                                component={Select}
                                nullOption={false}
                                propertyPath={'status'}
                                LookupFilterProps={{
                                    filterFunction: StatusLookupFilter,
                                    subscription: {
                                        value: true
                                    }
                                }}
                            />
                            <InputOnlyField
                                label="Sales Status" 
                                component={Select}
                                nullOption={false}
                                propertyPath={'salesStatus'}
                            />
                            <DollarInput
                                label="Cost Per Lot" 
                                propertyPath={'costPerLot'}
                            />
                            <InputOnlyField 
                                label="Precinct"
                                component={Select}
                                propertyPath={'projectPrecinctId'}
                                LookupFilterProps={{
                                    filterFunction: PrecinctLookupFilter,
                                    subscription: {
                                        initialValue: true
                                    }
                                }}
                            />
                            <InputOnlyField
                                label="Registration Date" 
                                component={DatePicker}
                                propertyPath={'registrationDate'}
                                onChange={e => handleRegistrationDateChange(e)}
                            />
                            <InputOnlyField
                                label="Registration Date Basis" 
                                component={Select}
                                propertyPath={'registrationDateBasis'}
                                onChange={event => handleRegistrationDateBasisChange(event.target.value)}
                            />
                            <InputOnlyField
                                label="Contract Sunset Date" 
                                component={DatePicker}
                                propertyPath={'contractSunsetDate'}                    
                            />
                            <DollarInput
                                label="FeasibilityPrice" 
                                propertyPath={'feasibilityPrice'}                    
                            />
                        </StyledFormColumn>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <InputOnlyField 
                                label="Refer Stage"
                                component={Select}
                                propertyPath={'referStage'}
                                LookupFilterProps={{
                                    filterFunction: StageLookupFilter,
                                    subscription: {
                                        initialValue: true
                                    }
                                }}
                                MenuProps={{
                                    style: {
                                        maxHeight: 400
                                    }
                                }}
                            />
                            <InputOnlyField
                                label="DA Approved Residential Lots"
                                component={Numeric}
                                propertyPath={'daApprovedLots'}
                            />
                            <InputOnlyField
                                label="Residential Development Sites"
                                component={Numeric}
                                propertyPath={'residentialSites'}
                            />
                            <InputOnlyField 
                                label="Potential Yield From Sites"
                                component={Numeric}
                                propertyPath={'additionalLots'}
                            />
                            <InputOnlyField 
                                label="No. of Special Lots"
                                component={Numeric}
                                propertyPath={'specialLots'}
                            />
                            <InputOnlyField 
                                label="Stage Image Url"
                                component={Text}
                                propertyPath={'imageUrl'}
                            />
                            <InputOnlyField
                                label="Sage Stage Code"
                                component={Text}
                                propertyPath={"sageStageCode"}
                            />
                            <InputOnlyField 
                                label="Sage Job Cost Code"
                                component={Text}
                                propertyPath={'sageJobCostCode'}
                            />
                        </StyledFormColumn>
                    </FormSection>
                    <StyledDivider />
                    {
                        permissions?.includes(Permissions.StageCanEditFinanceAttributes) &&
                        <FormSection>
                            <StyledFormColumn
                                size={COLUMN_SIZING}
                            >
                                <PercentInput
                                    label="GM"
                                    propertyPath={'gmPercent'}
                                />
                            </StyledFormColumn>
                        </FormSection>
                    }
                    <StyledDivider />
                    <FormSection>
                        <InputOnlyField 
                            label="Comments"
                            component={Text}
                            multiline
                            propertyPath={'comments'}
                            rows={5}
                        />
                    </FormSection>
                </StyledDialogContent>
                
                <StyledDialogActions>
                    <StyledFormSubmitButton variant="contained">Submit</StyledFormSubmitButton>
                    <StyledButton variant="contained" onClick={props.onClose}>Cancel</StyledButton>
                </StyledDialogActions>
            </StyledDialog>
        </ContextForm>
    );
}

export default StageForm;